/**
 * ATL-1523 — phone numbers as text: what is stored, and what a `wa.me` / `tel:` link is built from.
 *
 * A phone pasted from a contact card, a messenger or a spreadsheet often carries characters nobody
 * can see: bidirectional-text controls (U+202C POP DIRECTIONAL FORMATTING closes nearly every
 * number copied out of an iOS contact), zero-width spaces and joiners, a byte-order mark, and
 * no-break spaces instead of ordinary ones. On 2026-09-27, 406 live clients in 13 organizations had
 * them in `phone`, and the client card's WhatsApp button — which removed only ASCII spaces, `+`,
 * parentheses and dashes — built `https://wa.me/77021761144%E2%80%AC`, which WhatsApp rejects. The
 * tap looked dead.
 *
 * Two different questions, two functions:
 *   - `normalizePhoneText` — the text to STORE: the same text, with the invisible characters
 *     removed and any Unicode space written as a plain one — and NOTHING else. It does not trim or
 *     collapse spaces: the phone field holds notes as well ("+7700… на одном ковре…"), and a trailing
 *     space never broke anything (links are built from digits). Minimal on purpose, because the same
 *     function decides what the ATL-1523 backfill rewrites in both stores.
 *   - `phoneDigits` — the digits a link is built from, and nothing else.
 *
 * The characters are listed explicitly rather than as `\p{Cf}` / `\p{Zs}`: Unicode property escapes
 * are not guaranteed in the app's JavaScript engine (Hermes), and a regex that fails to compile there
 * would take down every screen that imports this file.
 */

/**
 * Invisible format characters (Unicode general category Cf, the ones that occur in practice):
 * soft hyphen, Arabic letter mark, Mongolian vowel separator, zero-width space / non-joiner / joiner,
 * left-to-right and right-to-left marks, the embedding/override controls U+202A–U+202E, word joiner
 * and invisible operators U+2060–U+2064, the isolates and deprecated controls U+2066–U+206F, and the
 * byte-order mark / zero-width no-break space.
 */
const INVISIBLE_FORMAT_CHARACTERS =
  /[\u00AD\u061C\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u206F\uFEFF]/g

/** Unicode spaces other than U+0020 (category Zs): no-break, en/em/thin/hair, narrow no-break, … */
const NON_ASCII_SPACES = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g

/**
 * The phone as it should be stored: invisible characters removed, Unicode spaces made plain.
 * Idempotent. A non-string is returned unchanged (the caller's field semantics — `undefined` "leave
 * alone", `null` "cleared" — are not this function's to decide).
 */
export function normalizePhoneText<T>(value: T): T | string {
  if (typeof value !== 'string') return value
  return value.replace(INVISIBLE_FORMAT_CHARACTERS, '').replace(NON_ASCII_SPACES, ' ')
}

/** Only the digits 0–9 of a phone, or '' when there are none (or the value is not a string). */
export function phoneDigits(value: unknown): string {
  return typeof value === 'string' ? value.replace(/[^0-9]/g, '') : ''
}

/**
 * The `https://wa.me/…` link for a phone, or null when there is nothing to open.
 * `text` is the message WhatsApp pre-fills; it is encoded as a query value (`encodeURIComponent`),
 * so an `&`, `#` or `?` inside it — a receipt link has all three — cannot end or corrupt the URL.
 * With `allowChatPicker`, a phone with no digits and a message opens `https://wa.me/?text=…`, which
 * lets the user pick the chat (the receipt modal relies on that: its phone field may be left empty).
 */
export function whatsappUrl(
  phone: unknown,
  text?: string,
  { allowChatPicker = false }: { allowChatPicker?: boolean } = {}
): string | null {
  const digits = phoneDigits(phone)
  const query = text ? `?text=${encodeURIComponent(text)}` : ''
  if (!digits) return allowChatPicker && query ? `https://wa.me/${query}` : null
  return `https://wa.me/${digits}${query}`
}
